
import {Component, Prop, Vue} from 'vue-property-decorator';
import vModal from './vModal.vue';
import {mapState} from "vuex";
import {LocalizationState} from "@/store/modules/LocalizationModule";
import {getNutrientOverrides} from "@/utils/API/RecipeNutrientOverrideRepository";
import SystemConfigurationRepository from "@/utils/API/SystemConfigurationRepository";
import {NestableNutrient} from "@/models/Nutrient/CategorizedNutrients";
import {NUTRIENTS_FETCH} from '@/store/ActionTypes';
import {NutrientType} from '@/models/Ingredient/NutrientType';
import {DailyValueCalculationMethod} from "@/models/Recipes/DailyValueCalculationMethod";
import {mapNutritions, round} from "@/utils/Helpers";
import VGenericConfirmationModal from "@/components/vGenericConfirmationModal.vue";
import { Recipe } from '@/models/Recipes/Recipe';
import {RecipeCalculate, RecipeCalculateIngredient, RecipeCalculateSubrecipe} from "@/models/Recipes/RecipeCalculate";
import type {Subrecipe} from "@/models/Recipes/Subrecipe";
import RecipesRepository from "@/utils/API/RecipesRepository";
import vPreLoader from "@/components/vPreLoader.vue";
import {Nutritions} from "@/models/Recipes/Nutritions";

@Component({
  components: {
    vPreLoader,
    VGenericConfirmationModal,
    vModal,
  },
  computed: {
    ...mapState({
      localizationState: (state: any) => state.LocalizationModule,
    })
  }
})
export default class vAdjustOverrideNutrientsForm extends Vue {
  localizationState: LocalizationState;
  overrideNutrients: {
    id: number,
    name: string,
    nutrientUnitId: number,
    overrideDV: number | string | null,
    overrideValue: number | string | null,
    percentDV: number,
    value: number
  }[] = [];
  isDisclaimerChecked: boolean = false;
  showOverrideConfirmationModal: boolean = false;
  DailyValueCalculationMethod = DailyValueCalculationMethod;
  isRecipeLoaded: boolean = false;
  round = round;
  nutritionAnalysis: Nutritions = new Nutritions(
      [],
      'g',
      0,
      0,
      0,
      0,
      0,
      null,
      null,
      null,
      null
  );

  @Prop() recipe: Recipe;
  @Prop() showLabelOverrideModal: boolean;
  @Prop() dvCalculationMethod: DailyValueCalculationMethod;
  @Prop() servingCount: number;
  @Prop() servingQuantityInUnitOfMeasurement: number;
  @Prop() recipeServingType: number;
  @Prop() selectedServingWeightMeasurement: any;
  @Prop() recipeMetadata: any;
  @Prop() calculationMethodModal: any;

  get translations() {
    return this.localizationState.translations;
  }

  closeLabelOverrideModal() {
    this.$emit("on-cancel");
  }

  openConfirmationModal() {
    this.showOverrideConfirmationModal = true;
  }

  closeConfirmationModal() {
    this.showOverrideConfirmationModal = false;
  }

  resetLabelOverrides() {
    this.overrideNutrients.forEach((override) => {
      override.overrideValue = "";
      override.overrideDV = "";
    });

    this.recipe.servingQuantityInUnitOfMeasurementOverride = null;
    this.recipe.servingUnitOfMeaurementOverride = null;
  }

  submitLabelOverrides() {
    const changedOverrides = this.overrideNutrients.map((override) => {
      return {
        id: override.id,
        recipeId: this.recipe.id,
        valueOverride: override.overrideValue,
        dailyValueOverride: override.overrideDV,
        nutrientUnitId: override.nutrientUnitId,
      };
    }).filter(
      (nutrient) => (nutrient.valueOverride !== null && nutrient.valueOverride !== '')
        || (nutrient.dailyValueOverride !== null && nutrient.dailyValueOverride !== ''));

    const removedOverrides = this.overrideNutrients
      .filter((override) => (
        !(override.id === null || override.id === 0)
        && (override.overrideValue === '' || override.overrideValue === null)
        && (override.overrideDV === '' || override.overrideDV === null)
      ))
      .map((override) => override.id);

    this.$emit("on-submit", {
      recipeId: this.recipe.id,
      changed: changedOverrides,
      removed: removedOverrides,
    });
  }

  flattenNutrient(nutrient: NestableNutrient) : NestableNutrient[] {
    const rv: NestableNutrient[] = [];

    if(nutrient.nutrientType != NutrientType.SugarAlcohols){
      const children = nutrient.childNutrients;
      nutrient.childNutrients = [];

      rv.push(nutrient );

      if (children) {
        children.forEach((childNutrient) => {
          rv.push(...this.flattenNutrient(childNutrient));
        });
      }
    }

    return rv;
  }

  initializeSourceNutrients(){
    let nutrientSourceGroup = this.$store.getters.getNutrients;

    if(!nutrientSourceGroup){
      this.$store
        .dispatch(NUTRIENTS_FETCH)
        .then((nutrients) => {
          nutrientSourceGroup = nutrients;
        })
        .catch((error) => {
          if (error.response) {
            this.$snotify.error(
              error.response.data.errorMessages[0].userVisibleMessage
            );
          }
          else this.$snotify.error(this.translations.get("network-error"));
        });
    }

    return nutrientSourceGroup;
  }

  resetModalData(){
    this.closeConfirmationModal();
    this.isDisclaimerChecked = false;
  }

  async fetchAndPopulateOverrides() {
    this.resetModalData();
    let existingOverrides = await getNutrientOverrides(this.recipe.id);
    let nutrients = (await SystemConfigurationRepository.listNutrients()).data.result;

    // Flatten the listNutrients call
    let flattenedNutrients: NestableNutrient[] = [];

    Object.values(nutrients).forEach((nestableNutrients) => {
      nestableNutrients.forEach((nutrient) => {
        flattenedNutrients.push(...this.flattenNutrient(nutrient));
      });
    });

    // Create a lookup map for faster nutrient name searches
    const nutrientLookup = new Map<string, any>();
    this.nutritionAnalysis.totalNutrientsPerServing.forEach((nutrient) => {
      nutrientLookup.set(nutrient.nutrientName, nutrient);
    });

    // Initialize overrideNutrients
    this.overrideNutrients = flattenedNutrients.map((nutrient) => {
      let nutrientData = this.searchNutrientDataUsingMap(nutrient.name, nutrientLookup);

      if(nutrientData) {
        let existingOverride = existingOverrides.data.result.find(
          (override) => override.nutrientType === nutrient.nutrientType
        );

        return {
          id: existingOverride === undefined ? null : existingOverride.id,
          nutrientUnitId: existingOverride.nutrientUnitId,
          name: existingOverride.displayText, //nutrient.name,
          value: nutrientData.roundedValue,
          overrideValue: existingOverride ? existingOverride.valueOverride : null,
          percentDV: nutrientData.roundedDailyValue,
          overrideDV: existingOverride ? existingOverride.dailyValueOverride : null,
        };
      }
    }).filter((override) => override !== undefined);
  }

  get altNameMapping(): Record<string, string> {
    return {
      Omega3FattyAcids: 'Omega3',
      Omega6FattyAcids: 'Omega6',
      Threonine : 'LThreonine',
      Isoleucine : 'LIsoleucine',
      Leucine : 'LLeucine',
      Lysine : 'LLysine',
      Valine : 'LValine',
      Phenylalanine : 'LPhenylalanine',
      Tyrosine : 'LTyrosine',
      Cysteine : 'LCysteine',
      Methionine : 'LMethionine',
      Serine : 'LSerine',
      Proline : 'LProline',
      Alanine : 'LAlanine',
      AsparticAcid : 'LAsparticAcid',
      GlutamicAcid : 'LGlutamicAcid',
      Glutamine : 'LGlutamine',
      Glycine : 'LGlycine',
      Histidine : 'LHistidine',
      Asparagine : 'LAsparagine',
      Arginine : 'LArginine',
      AddedSugars : 'AddedSugar',
    };
  }

  // Optimized nutrient data search using a Map
  searchNutrientDataUsingMap(nameKey: string, lookupMap: Map<string, any>) {
    // Normalize nameKey removing spaces
    const spaceRemovedKey = nameKey.replace(/\s+/g, '');
    let nutrient = lookupMap.get(spaceRemovedKey);

    if (!nutrient) {
      // If nutrient is not found, try using the alternative mapping
      const altName = this.altNameMapping[spaceRemovedKey];
      if (altName) {
        nutrient = lookupMap.get(altName);
      }
    }

    if (nutrient) {
      return nutrient.originalNutrientAnalysis || nutrient;
    }
    return null;
  }

  calculateNutritions() {
    this.resetModalData();
    let childRecipeIds: RecipeCalculateSubrecipe[] = [];

    this.recipe.childRecipes.map((child: Subrecipe) =>
      childRecipeIds.push({
        recipeId: child.recipe.id,
        quantity: child.quantity ? child.quantity : 1,
        foodGroupClassificationId: child.foodGroupClassificationId != null
          ? child.foodGroupClassificationId
          : 0,
        isAddedSugar: child.isAddedSugar,
        recipeCompositionMeasurementId: child.recipeCompositionMeasurementId,
      })
    );

    let ingredients: RecipeCalculateIngredient[] = [];

    this.recipe.ingredients.map((ingr: any) => {
      ingredients.push({
        quantity: ingr.quantity,
        ingredientMeasurementId: ingr.measurement.id,
        isAddedSugar: ingr.isAddedSugar,
        foodGroupClassificationId:
          ingr.foodGroupClassificationId != null
            ? ingr.foodGroupClassificationId
            : 0,
      });
    });
    new Promise((resolve, reject) => {
      let calcRecipe: RecipeCalculate = {
        recipeId: this.recipe.id,
        recipes: childRecipeIds,
        ingredients: ingredients,
        servings: Math.round(this.servingCount * 100) / 100,
        lossAmount:
          this.recipe.lossAmount != null
            ? round(this.recipe.lossAmount, 2)
            : 0,
        lossType:
          this.recipe.lossType != null
            ? this.recipe.lossType
            : 0,
        cookingMethodId:
          this.recipe.cookingMethodId != null
            ? this.recipe.cookingMethodId
            : 0,
        recipeServingType: this.recipeServingType,
        caloriesCalculationMethod: this.calculationMethodModal.selectedCalculationMethod,
        dailyValueCalculationMethod: this.calculationMethodModal.selectedDailyValueCalculation,
        referenceAmount : this.recipe.referenceAmount,
        recipeProductType : this.recipe.recipeProductType,
        ruleIndex: this.recipeMetadata.roundingRuleIndex,
        nutrientTypesToCalculate: null
      };
      if (this.recipeServingType === 0) {
        calcRecipe.servingCount = Math.round(this.servingCount * 100) / 100;
      }
      else {
        calcRecipe.servingQuantityInUnitOfMeasurement =
          Math.round(this.servingQuantityInUnitOfMeasurement * 100) / 100;
        calcRecipe.servingUnitOfMeasurement =
          this.selectedServingWeightMeasurement.name;
      }
      RecipesRepository.calculate(calcRecipe)
        .then((response) => {
          this.nutritionAnalysis = mapNutritions(
            response.data.result
          );
          this.servingCount =
            Math.round(response.data.result.servings * 100) / 100;

          this.fetchAndPopulateOverrides();
          this.isRecipeLoaded = true;
        })
        .catch((error) => {
          let message = "";
          if (error.response) {
            message = error.response.data.errorMessages[0].userVisibleMessage;
          }
          else {
            message = this.translations.get("network_error");
          }
          reject({ body: message });
        });
    });
  }
}
